<template>
  <AppHeader />
  <div class="min-h-screen bg-blue-50">
    <!-- Header section with UP colors -->
    <div class="bg-gradient-to-r from-blue-900 to-blue-700 py-8 shadow-md">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex flex-col items-center">
          <div class="text-center">
            <h1 class="text-2xl font-bold text-white mb-1">My Lectures</h1>
            <p class="text-blue-100">Manage your weekly class schedule</p>
            <div v-if="isAdmin" class="mt-2 bg-yellow-400 text-blue-900 px-3 py-1 rounded-full text-xs font-medium inline-flex items-center">
              <span class="material-symbols-outlined text-sm mr-1">admin_panel_settings</span>
              Admin Mode
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Main content -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <!-- Back button -->
      <div class="mb-4">
        <router-link to="/timetable" class="inline-flex items-center text-blue-900 hover:text-blue-700">
          <span class="material-symbols-outlined mr-1">arrow_back</span>
          Back to Timetables
        </router-link>
      </div>

      <!-- Toggle View -->
      <div class="flex justify-end mb-4">
        <div class="inline-flex rounded-md shadow-sm" role="group">
          <button
            @click="viewMode = 'list'"
            :class="[
              'px-4 py-2 text-sm font-medium border border-blue-900',
              viewMode === 'list' 
                ? 'bg-blue-900 text-white' 
                : 'bg-white text-blue-900 hover:bg-blue-50'
            ]"
            class="rounded-l-lg"
          >
            <span class="flex items-center">
              <span class="material-symbols-outlined mr-1">list</span>
              List
            </span>
          </button>
          <button
            @click="viewMode = 'calendar'"
            :class="[
              'px-4 py-2 text-sm font-medium border border-blue-900 border-l-0',
              viewMode === 'calendar' 
                ? 'bg-blue-900 text-white' 
                : 'bg-white text-blue-900 hover:bg-blue-50'
            ]"
            class="rounded-r-lg"
          >
            <span class="flex items-center">
              <span class="material-symbols-outlined mr-1">calendar_month</span>
              Calendar
            </span>
          </button>
        </div>
      </div>

      <!-- List View -->
      <div v-if="viewMode === 'list'" class="bg-white rounded-lg shadow p-6">

        <!-- Classes List -->
        <div >
          <div class="flex justify-between items-center mb-4">
            <h2 class="text-lg font-semibold text-gray-800">My Lectures</h2>
            <div class="flex items-center">
              <button 
                v-if="!showAddForm && !editingClass"
                @click="showAddForm = true"
                class="px-4 py-2 bg-blue-900 text-white rounded-lg hover:bg-blue-800 transition-colors flex items-center"
              >
                <span class="material-symbols-outlined mr-1">add</span>
                Add Lecture
              </button>
              <button 
                v-else
                @click="cancelFormAction"
                class="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center"
              >
                <span class="material-symbols-outlined mr-1">close</span>
                Cancel
              </button>
            </div>
          </div>

          <!-- Next Class Smart Card -->
          <div v-if="nextClassInfo" class="bg-blue-50 border-l-4 border-blue-700 rounded-lg p-4 mb-4 flex items-center gap-4 shadow-sm">
            <span class="material-symbols-outlined text-3xl text-blue-700">schedule</span>
            <div>
              <div class="font-semibold text-blue-900 text-base mb-1">
                Next class: {{ nextClassInfo.classItem.moduleCode }} 
              </div>
              <div class="text-sm text-blue-800">
                {{ nextClassInfo.classItem.day }}, {{ nextClassInfo.classItem.startTime }} - {{ nextClassInfo.classItem.endTime }} at {{ nextClassInfo.classItem.venue }}
              </div>
              <div class="mt-1">
                <span
                  :class=" [
                    'inline-block px-3 py-1 rounded-full text-xs font-semibold',
                    nextClassInfo.ms <= 5 * 60 * 1000
                      ? 'bg-red-500 text-white animate-pulse'
                      : nextClassInfo.ms <= 15 * 60 * 1000
                        ? 'bg-yellow-400 text-gray-900'
                        : 'bg-green-500 text-white'
                  ]"
                  v-if="typeof nextClassInfo.ms === 'number'"
                >
                  {{ nextClassInfo.status }}
                </span>
                <span v-else class="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-gray-200 text-gray-500">
                  {{ nextClassInfo.status }}
                </span>
              </div>
            </div>
          </div>

          <!-- Add/Edit Class Form (conditionally shown) -->
          <div v-if="showAddForm || editingClass" class="mb-6 border-b border-gray-200 pb-6">
            <class-entry-form
              :edit-class="editingClass"
              :user-modules="userModules"
              @class-added="handleClassAdded"
              @class-updated="handleClassUpdated"
              @cancel-edit="cancelFormAction"
              @add-module="goToAddModule"
            />
          </div>

          <!-- Loading State -->
          <div v-if="loadingClasses" class="flex justify-center py-8">
            <div class="w-8 h-8 border-4 border-blue-200 border-t-blue-800 rounded-full animate-spin"></div>
          </div>

          <!-- Empty State -->
          <div v-else-if="userClasses.length === 0" class="text-center py-8">
            <span class="material-symbols-outlined text-5xl text-gray-300 mb-3">event_busy</span>
            <h3 class="text-lg font-medium text-gray-700 mb-2">No lectures added yet</h3>
            <p class="text-gray-500 max-w-md mx-auto mb-6">
              Add your lectures to keep track of your weekly schedule.
            </p>
            <button 
              @click="showAddForm = true"
              class="px-6 py-3 bg-blue-900 text-white rounded-lg hover:bg-blue-800 transition-colors"
            >
              <span class="flex items-center">
                <span class="material-symbols-outlined mr-2">add</span>
                Add Your First Lecture
              </span>
            </button>
          </div>

          <!-- Classes by Day -->
          <div v-else class="space-y-6">
            <div 
              v-for="(classes, day) in userClassesByDay" 
              :key="day"
              v-show="classes.length > 0"
              class="border-b border-gray-200 pb-4 last:border-0 last:pb-0"
            >
              <h3 class="font-medium text-gray-800 mb-3">{{ day }}</h3>
              
              <div class="space-y-2">
                <div 
                  v-for="classItem in classes" 
                  :key="classItem.id"
                  class="flex items-center justify-between p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div class="flex-1">
                    <div class="font-medium text-gray-900 text-left">
                      {{ classItem.moduleCode }}
                    </div>
                    <div class="text-sm text-gray-600 mt-1 text-left">
                      {{ classItem.moduleName }} • {{ classItem.lecturer }}
                      {{ classItem.startTime }} - {{ classItem.endTime }} • {{ classItem.venue }} • {{ classItem.type }}
                    </div>
                    <div v-if="classItem.notes" class="text-xs text-gray-500 mt-1 italic text-left">
                      {{ classItem.notes }}
                    </div>
                  </div>
                  
                  <div class="flex space-x-2">
                    <button 
                      @click="editClass(classItem)"
                      class="p-2 text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                      title="Edit Class"
                    >
                      <span class="material-symbols-outlined">edit</span>
                    </button>
                    <button 
                      @click="confirmDeleteClass(classItem)"
                      class="p-2 text-red-600 hover:bg-red-50 rounded-full transition-colors"
                      title="Delete Class"
                    >
                      <span class="material-symbols-outlined">delete</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Calendar View -->
      <div v-else-if="viewMode === 'calendar'" class="bg-white rounded-lg shadow p-6">
        <div class="flex justify-between items-center mb-4">
          <h2 class="text-lg font-semibold text-gray-800">Lecture Calendar</h2>
          <div class="flex items-center space-x-2" v-if="userClasses.length > 0">
            <button 
              v-if="!showAddForm && !editingClass"
              @click="showAddForm = true; viewMode = 'list'"
              class="px-4 py-2 bg-blue-900 text-white rounded-lg hover:bg-blue-800 transition-colors flex items-center"
            >
              <span class="material-symbols-outlined mr-1">add</span>
              Add Lecture
            </button>
          </div>
        </div>
        
        <lecture-calendar
          v-if="!loadingClasses && userClasses.length > 0"
          :lectures="formattedClasses"
          @edit-lecture="editClass"
        />
        
        <div v-else-if="loadingClasses" class="flex justify-center py-8">
          <div class="w-8 h-8 border-4 border-blue-200 border-t-blue-800 rounded-full animate-spin"></div>
        </div>
        
        <div v-else class="text-center py-8 mt-4">
          <span class="material-symbols-outlined text-5xl text-gray-300 mb-3">event_busy</span>
          <h3 class="text-lg font-medium text-gray-700 mb-2">No lectures added yet</h3>
          <p class="text-gray-500 max-w-md mx-auto mb-6">
            Add your lectures to view them in the calendar.
          </p>
          <button 
            @click="showAddForm = true; viewMode = 'list'"
            class="px-6 py-3 bg-blue-900 text-white rounded-lg hover:bg-blue-800 transition-colors"
          >
            <span class="flex items-center">
              <span class="material-symbols-outlined mr-2">add</span>
              Add Your First Lecture
            </span>
          </button>
        </div>
      </div>

      <!-- Class Deletion Confirmation -->
      <div v-if="classToDelete" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div class="bg-white rounded-lg p-5 max-w-md w-full shadow-xl">
          <div class="flex justify-between items-start mb-4">
            <div class="flex items-center">
              <span class="material-symbols-outlined text-red-600 mr-2">warning</span>
              <h3 class="font-semibold text-lg">Delete Class</h3>
            </div>
            <button @click="classToDelete = null" class="text-gray-500 hover:text-gray-700">
              <span class="material-symbols-outlined">close</span>
            </button>
          </div>
          <p class="mb-4">
            Are you sure you want to delete this class? This action cannot be undone.
          </p>
          <p class="font-medium mb-6">
            {{ classToDelete?.moduleCode }} ({{ classToDelete?.day }}, {{ classToDelete?.startTime }} - {{ classToDelete?.endTime }})
          </p>
          <div class="flex justify-end space-x-3">
            <button 
              @click="classToDelete = null"
              class="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
            >
              Cancel
            </button>
            <button 
              @click="deleteClass"
              class="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
  <BottomNav />
</template>

<script>
import AppHeader from '@/components/common/AppHeader.vue';
import BottomNav from '@/components/common/BottomNav.vue';
import ClassEntryForm from '@/components/timetable/ClassEntryForm.vue';
import LectureCalendar from '@/components/timetable/LectureCalendar.vue';
import { mapState, mapGetters } from 'vuex';
import { addUserClass, updateUserClass, deleteUserClass } from '@/composables/useFirestore';

export default {
  components: {
    AppHeader,
    BottomNav,
    ClassEntryForm,
    LectureCalendar
  },
  data() {
    return {
      showAddForm: false,
      editingClass: null,
      classToDelete: null,
      loadingClasses: false,
      viewMode: 'list',
      nextClassNow: new Date(),
      nextClassInterval: null
    };
  },
  computed: {
    ...mapState(['user', 'profile', 'userClasses']),
    ...mapGetters(['userClassesByDay']),
    
    // Add computed property for user modules
    userModules() {
      // Get modules from user profile, return empty array if not available
      return this.profile && this.profile.university && 
             Array.isArray(this.profile.university.modules) ? 
             this.profile.university.modules : [];
    },
    
    isAuthenticated() {
      return this.user && this.user.uid;
    },
    
    isAdmin() {
      return this.profile && this.profile.role === 'admin';
    },

    formattedClasses() {
      // Format userClasses for the calendar component
      if (!this.userClasses) return [];
      
      return this.userClasses.map(cls => ({
        id: cls.id,
        title: cls.moduleCode,
        description: cls.moduleName || '',
        day: cls.day,
        startTime: cls.startTime,
        endTime: cls.endTime,
        venue: cls.venue,
        type: cls.type,
        lecturer: cls.lecturer || '',
        notes: cls.notes || '',
        // Add color based on module code for consistency
        color: this.getColorForModule(cls.moduleCode)
      }));
    },

    nextClassInfo() {
      if (!this.userClasses || this.userClasses.length === 0) return null;
      const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
      const now = this.nextClassNow || new Date();
      const todayIdx = now.getDay() === 0 ? 6 : now.getDay() - 1;

      const allClasses = this.userClasses.map(cls => {
        const dayIdx = daysOfWeek.indexOf(cls.day);
        if (dayIdx === -1) return null;
        let start = cls.startTime;
        if (start && start.includes('-')) start = start.split('-')[0].trim();
        const [h, m] = start.split(':').map(Number);
        let classDate = new Date(now);
        classDate.setHours(h, m, 0, 0);
        classDate.setDate(now.getDate() + ((dayIdx - todayIdx + 7) % 7));
        if (dayIdx === todayIdx && classDate < now) classDate.setDate(classDate.getDate() + 7);
        return { ...cls, _classDate: classDate, _dayIdx: dayIdx };
      }).filter(Boolean);

      const next = allClasses.reduce((acc, cls) => {
        if (!acc || cls._classDate < acc._classDate) return cls;
        return acc;
      }, null);

      if (!next) return null;

      const diffMs = next._classDate - now;
      if (diffMs < 0) return null;

      // Calculate days, hours, minutes, seconds
      let remaining = diffMs;
      const days = Math.floor(remaining / (1000 * 60 * 60 * 24));
      remaining -= days * (1000 * 60 * 60 * 24);
      const hours = Math.floor(remaining / (1000 * 60 * 60));
      remaining -= hours * (1000 * 60 * 60);
      const minutes = Math.floor(remaining / (1000 * 60));
      remaining -= minutes * (1000 * 60);
      const seconds = Math.floor(remaining / 1000);

      // Build string, omitting zero values except for seconds
      const parts = [];
      if (days > 0) parts.push(`${days} day${days > 1 ? 's' : ''}`);
      if (hours > 0) parts.push(`${hours} hour${hours > 1 ? 's' : ''}`);
      if (minutes > 0) parts.push(`${minutes} min${minutes > 1 ? 's' : ''}`);
      parts.push(`${seconds} sec${seconds !== 1 ? 's' : ''}`);

      return {
        classItem: next,
        ms: diffMs,
        status: diffMs === 0 ? 'Now' : `Starts in ${parts.join(' ')}`
      };
    }
  },
  created() {
    if (this.$route.query.upload === 'true') {
      this.showAddForm = true;
    }
  },
  mounted() {
    if (this.isAuthenticated) {
      this.loadUserClasses();
    }
    // Start interval for real-time countdown
    this.nextClassInterval = setInterval(() => {
      this.nextClassNow = new Date();
    }, 1000);
  },
  beforeUnmount() {
    if (this.nextClassInterval) {
      clearInterval(this.nextClassInterval);
    }
  },
  methods: {
    async loadUserClasses() {
      this.loadingClasses = true;
      try {
        await this.$store.dispatch('fetchUserClasses');
      } catch (error) {
        console.error('Error loading classes:', error);
        this.$store.dispatch('showAlert', {
          message: 'Failed to load your classes',
          type: 'error'
        });
      } finally {
        this.loadingClasses = false;
      }
    },
    
    async handleClassAdded(classData) {
      if (!this.isAuthenticated) {
        this.$store.dispatch('showAlert', {
          message: 'You must be logged in to add classes',
          type: 'error'
        });
        return;
      }
      
      try {
        console.log('Adding class with data:', classData);
        // Make sure we have valid data before trying to add it
        if (!classData || !classData.moduleCode || !classData.day || 
            !classData.startTime || !classData.endTime || !classData.venue || !classData.type) {
          throw new Error('Missing required class data');
        }
        
        const result = await addUserClass(this.user.uid, classData);
        
        if (result.success) {
          // Add the class with its ID to the local state
          const newClass = { ...classData, id: result.id };
          this.$store.commit('addClass', newClass);
          
          this.$store.dispatch('showAlert', {
            message: 'Class added successfully',
            type: 'success'
          });
          
          this.showAddForm = false;
        } else {
          throw new Error(result.message || 'Failed to add class');
        }
      } catch (error) {
        console.error('Error adding class:', error);
        this.$store.dispatch('showAlert', {
          message: `Failed to add class: ${error.message}`,
          type: 'error'
        });
      }
    },
    
    async handleClassUpdated(classData) {
      if (!this.isAuthenticated || !classData.id) {
        this.$store.dispatch('showAlert', {
          message: 'Invalid operation',
          type: 'error'
        });
        return;
      }
      
      try {
        const result = await updateUserClass(this.user.uid, classData.id, classData);
        
        if (result.success) {
          this.$store.commit('updateClass', classData);
          
          this.$store.dispatch('showAlert', {
            message: 'Class updated successfully',
            type: 'success'
          });
          
          this.editingClass = null;
        } else {
          throw new Error(result.message || 'Failed to update class');
        }
      } catch (error) {
        console.error('Error updating class:', error);
        this.$store.dispatch('showAlert', {
          message: `Failed to update class: ${error.message}`,
          type: 'error'
        });
      }
    },
    
    editClass(classItem) {
      this.editingClass = { ...classItem };
      this.showAddForm = false;
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    
    cancelFormAction() {
      this.editingClass = null;
      this.showAddForm = false;
    },
    
    confirmDeleteClass(classItem) {
      this.classToDelete = classItem;
    },
    
    async deleteClass() {
      if (!this.isAuthenticated || !this.classToDelete?.id) {
        this.classToDelete = null;
        return;
      }
      
      try {
        const result = await deleteUserClass(this.user.uid, this.classToDelete.id);
        
        if (result.success) {
          this.$store.commit('removeClass', this.classToDelete.id);
          
          this.$store.dispatch('showAlert', {
            message: 'Class deleted successfully',
            type: 'success'
          });
        } else {
          throw new Error(result.message || 'Failed to delete class');
        }
      } catch (error) {
        console.error('Error deleting class:', error);
        this.$store.dispatch('showAlert', {
          message: `Failed to delete class: ${error.message}`,
          type: 'error'
        });
      } finally {
        this.classToDelete = null;
      }
    },
    
    goToAddModule() {
      // Navigate to the UserUpdateUniversity page
      this.$router.push({
        path: '/update-university',
        query: { addModule: 'true' } // Pass query parameter to indicate adding module
      });
    },
    
    getColorForModule(moduleCode) {
      // Generate a consistent color based on the module code
      const colorIndex = moduleCode.split('').reduce((sum, char) => sum + char.charCodeAt(0), 0) % 6;
      
      const colors = [
        'bg-blue-100 text-blue-800 border-blue-300',
        'bg-green-100 text-green-800 border-green-300',
        'bg-yellow-100 text-yellow-800 border-yellow-300',
        'bg-purple-100 text-purple-800 border-purple-300',
        'bg-pink-100 text-pink-800 border-pink-300',
        'bg-indigo-100 text-indigo-800 border-indigo-300'
      ];
      
      return colors[colorIndex];
    }
  }
};
</script>

<style scoped>
@keyframes pulse {
  0%, 100% {
    width: 25%;
  }
  50% {
    width: 75%;
  }
}

.animate-pulse {
  animation: pulse 1.5s ease-in-out infinite;
}
</style>
